/**
 * Favorite Button Component
 * Toggle bookmark/favorite status for meetings
 */

import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useFavoriteMeetingIds, useToggleMeetingFavorite } from '@/hooks/meeting/useMeetingFavorites';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  meetingId: string;
  size?: 'sm' | 'md';
  className?: string;
}

export const FavoriteButton = ({ meetingId, size = 'md', className }: FavoriteButtonProps) => {
  const { user } = useAuth();
  const { data: favoriteIds, isLoading: isLoadingFavorites } = useFavoriteMeetingIds();
  const { mutate: toggleFavorite, isPending } = useToggleMeetingFavorite();

  const isFavorite = favoriteIds?.has(meetingId) ?? false;
  const isDisabled = isLoadingFavorites || isPending || !user;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isDisabled) {
      toggleFavorite({ meetingId, isFavorite });
    }
  };

  // Don't render if user not authenticated
  if (!user) return null;

  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const buttonSize = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            buttonSize,
            'transition-all duration-200',
            isFavorite && 'text-yellow-500 hover:text-yellow-600',
            !isFavorite && 'text-muted-foreground hover:text-yellow-500',
            className
          )}
          onClick={handleClick}
          disabled={isDisabled}
          aria-label={isFavorite ? 'Bỏ yêu thích' : 'Thêm vào yêu thích'}
        >
          <Star
            className={cn(
              iconSize,
              'transition-all duration-200',
              isFavorite && 'fill-yellow-500',
              isPending && 'animate-pulse'
            )}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{isFavorite ? 'Bỏ yêu thích' : 'Thêm vào yêu thích'}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default FavoriteButton;
